import React, { Suspense, lazy, useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Tabs,
  Tab,
  Typography,
  Switch,
  Autocomplete,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Table,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { GlobalSettingsManager, PermissionManagementManager } from '../../APIManager'
import { type GlobalSettingsPatch } from '../../APIManager/GlobalSettingsManager'
import TabbedRoleManagement from '../PermissionManagement/components/TabbedRoleManagement'
import ReportViewerRolesTab from './ReportViewerRolesTab'
import { PermissionManagementHeader } from '../PermissionManagement/PermissionManagementHeader'
import { transformRoles } from '../PermissionManagement/utils'
import { type PermissionItem } from '../PermissionManagement/types'
import GenericButton from '../../components/GenericButton'
import { ButtonType } from '../../components/GenericButton/styles'
import LoadingPageV2 from '../../pages/LoadingPageV2'
import { useAppSearchParams } from '../../hooks/useAppSearchParams'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import CustomTooltip from '../../components/CustomTooltip'
import { grey } from '../../colors'
import api from '../../utils/api'
import { notification } from '../../components/Notifications'
import { getTaskTriggerToastMessage } from '../../utils/taskTriggerToast'
import { useUserRole } from '../../hooks/useUserRole'
import { env } from '../../env'

const AppTable = lazy(async () => await import('../../components/CustomTable'))

enum TabKeys {
  INSTITUTIONAL_SETTINGS = 'General',
  ROLE_MANAGEMENT = 'System Level Role Management',
  PERMISSION_MANAGEMENT = 'Permission Management',
  REPORT_VIEWER_ROLES = 'Report Viewer Roles'
}

const TAB_PARAM = 'tab'

const CROSS_LISTING_LABELS: Record<string, string> = {
  PRIMARY_VIEWS_BOTH_SECONDARY_VIEWS_SELF:
    'Primary Instructor sees all responses, Secondary Instructor sees only their own',
  PRIMARY_AND_SECONDARY_VIEW_BOTH:
    'Both Primary and Secondary Instructors see all responses'
}

const LABEL_CELL = { border: 'none', py: 2.5, width: '45%', verticalAlign: 'middle' } as const
const CONTROL_CELL = { border: 'none', py: 2.5, verticalAlign: 'middle' } as const

const LabelWithHint: React.FC<{ label: string, hint: React.ReactNode }> = ({ label, hint }) => (
  <Box display='flex' alignItems='center' gap={0.5}>
    <Typography fontWeight={600}>{label}</Typography>
    <CustomTooltip title={hint} placement='right'>
      <HelpOutlineIcon sx={{ fontSize: 16, color: grey[500], cursor: 'pointer' }} />
    </CustomTooltip>
  </Box>
)

const toFormState = (s: {
  timezone: string | null
  crossListing: string | null
  enableSisUserOverride: boolean | null
  isCustomBrandingEnabled: boolean | null
  enableRepeatPerInstructorMerging: boolean | null
  defaultCognitoProvisioned: boolean | null
  isLiveReportsEnabled: boolean | null
}): GlobalSettingsPatch => ({
  timezone: s.timezone ?? '',
  crossListing: s.crossListing ?? '',
  enableSisUserOverride: s.enableSisUserOverride ?? false,
  isCustomBrandingEnabled: s.isCustomBrandingEnabled ?? false,
  enableRepeatPerInstructorMerging: s.enableRepeatPerInstructorMerging ?? false,
  defaultCognitoProvisioned: s.defaultCognitoProvisioned ?? false,
  isLiveReportsEnabled: s.isLiveReportsEnabled ?? false
})

const EMPTY_FORM: GlobalSettingsPatch = {
  timezone: '',
  crossListing: '',
  enableSisUserOverride: false,
  isCustomBrandingEnabled: false,
  enableRepeatPerInstructorMerging: false,
  defaultCognitoProvisioned: false,
  isLiveReportsEnabled: false
}

const getTimezoneOffsetMinutes = (tz: string): number => {
  try {
    const offsetStr = new Intl.DateTimeFormat('en', {
      timeZone: tz,
      timeZoneName: 'shortOffset'
    }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value ?? ''
    const match = offsetStr.match(/GMT([+-])(\d+)(?::(\d+))?/)
    if (!match) return 0
    const sign = match[1] === '+' ? 1 : -1
    return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3] ?? '0', 10))
  } catch {
    return 0
  }
}

const getTimezoneLabel = (tz: string): string => {
  try {
    const offset = new Intl.DateTimeFormat('en', {
      timeZone: tz,
      timeZoneName: 'shortOffset'
    }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value ?? ''
    return `(${offset}) ${tz}`
  } catch {
    return tz
  }
}

const InstitutionalSettingsFeature: React.FC = () => {
  const queryClient = useQueryClient()

  // ── Tab state ──
  const { searchParams, setUrlParams } = useAppSearchParams()
  const activeTab = (searchParams.get(TAB_PARAM) ?? TabKeys.INSTITUTIONAL_SETTINGS) as TabKeys

  useEffect(() => {
    if (!searchParams.get(TAB_PARAM)) {
      setUrlParams({ [TAB_PARAM]: TabKeys.INSTITUTIONAL_SETTINGS }) // defaults to "General"
    }
  }, [searchParams, setUrlParams])

  const handleTabChange = (_: React.SyntheticEvent, value: string): void => {
    setUrlParams({ [TAB_PARAM]: value })
  }

  // ── Permission Management state ──
  const [selectedPermissionEntity, setSelectedPermissionEntity] = useState<string | null>(null)

  const { data: permissionCdgData, isFetching: isPermissionCdgLoading } = useQuery({
    queryKey: ['permissionCdg', selectedPermissionEntity],
    enabled: Boolean(selectedPermissionEntity),
    queryFn: async () => {
      if (!selectedPermissionEntity) return null
      const { data } = await PermissionManagementManager.getPermissionCdg(selectedPermissionEntity)
      return data
    }
  })

  const listWithReadActionOnly = useMemo(() => {
    if (!permissionCdgData) return null
    return {
      ...permissionCdgData,
      data:
        permissionCdgData?.data?.map((item: PermissionItem) => {
          const roles = item.data?.roles || item.roles || []
          const transformedRoles = transformRoles(roles)
          return { ...item, roles: transformedRoles, data: { ...item.data, roles: transformedRoles } }
        }) ?? []
    }
  }, [permissionCdgData])

  const tableHeader = permissionCdgData?.header?.headerName ?? 'Permission Management'

  // ── Global Settings state ──
  const { data: settings, isLoading: isSettingsLoading } = useQuery({
    queryKey: ['globalSettings'],
    queryFn: async () => {
      const { data } = await GlobalSettingsManager.getGlobalSettings()
      return data
    }
  })

  const [formState, setFormState] = useState<GlobalSettingsPatch>(EMPTY_FORM)
  const [originalSettings, setOriginalSettings] = useState<GlobalSettingsPatch>(EMPTY_FORM)
  const [crossListingOptions, setCrossListingOptions] = useState<string[]>([])

  useEffect(() => {
    if (settings) {
      const s = toFormState(settings)
      setFormState(s)
      setOriginalSettings(s)
      setCrossListingOptions(settings.crossListingOptions ?? [])
    }
  }, [settings])

  const isDirty = JSON.stringify(formState) !== JSON.stringify(originalSettings)

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: GlobalSettingsManager.patchGlobalSettings,
    onSuccess: (response) => {
      const updated = toFormState(response.data)
      setFormState(updated)
      setOriginalSettings(updated)
      void queryClient.invalidateQueries({ queryKey: ['globalSettings'] })
    }
  })

  const handleChange = <K extends keyof GlobalSettingsPatch>(
    key: K,
    value: GlobalSettingsPatch[K]
  ): void => {
    setFormState(prev => ({ ...prev, [key]: value }))
  }

  const handleCancel = (): void => { setFormState(originalSettings) }
  const handleSave = (): void => { save(formState) }

  const { isGlobalRole } = useUserRole()
  const showDevTools = isGlobalRole && env.REACT_APP_AWS_ENVIRONMENT !== 'prod-classranked'
  const [isSisTriggering, setIsSisTriggering] = useState(false)

  const handleSisTrigger = async (): Promise<void> => {
    setIsSisTriggering(true)
    try {
      const result = await api.post<{ taskId?: string, task_id?: string, message?: string }>('/api/tasks/trigger/sis_flatfile_task/')
      notification(getTaskTriggerToastMessage(result.data), { variant: 'success' })
    } catch {
      notification('Failed to trigger SIS Sync task', { variant: 'error' })
    } finally {
      setIsSisTriggering(false)
    }
  }

  const timezones = useMemo(() => {
    let tzList: string[] = []
    try {
      tzList = 'supportedValuesOf' in Intl
        ? (Intl as unknown as { supportedValuesOf: (key: string) => string[] }).supportedValuesOf('timeZone')
        : settings?.timezone ? [settings.timezone] : []
    } catch {
      tzList = settings?.timezone ? [settings.timezone] : []
    }
    const offsets = new Map(tzList.map(tz => [tz, getTimezoneOffsetMinutes(tz)]))
    return [...tzList].sort((a, b) => (offsets.get(a) ?? 0) - (offsets.get(b) ?? 0))
  }, [settings?.timezone])

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2, mb: 4 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label='Institutional Settings Tabs'>
          <Tab label={TabKeys.INSTITUTIONAL_SETTINGS} value={TabKeys.INSTITUTIONAL_SETTINGS} sx={{ fontWeight: 600 }} />
          <Tab label={TabKeys.ROLE_MANAGEMENT} value={TabKeys.ROLE_MANAGEMENT} sx={{ fontWeight: 600 }} />
          <Tab label={TabKeys.PERMISSION_MANAGEMENT} value={TabKeys.PERMISSION_MANAGEMENT} sx={{ fontWeight: 600 }} />
          <Tab label={TabKeys.REPORT_VIEWER_ROLES} value={TabKeys.REPORT_VIEWER_ROLES} sx={{ fontWeight: 600 }} />
        </Tabs>
      </Box>

      {activeTab === TabKeys.INSTITUTIONAL_SETTINGS && (
        isSettingsLoading
          ? <LoadingPageV2 />
          : <Box>
          <Table sx={{ '& .MuiTableCell-root': { borderBottom: 'none' } }}>
            <TableBody>
              <TableRow>
                <TableCell sx={LABEL_CELL}>
                  <LabelWithHint
                    label='Institution Time Zone'
                    hint='Institution timezone for all default dates across the ClassRanked platform.'
                  />
                </TableCell>
                <TableCell sx={CONTROL_CELL}>
                  <Autocomplete
                    options={timezones}
                    value={formState.timezone ?? ''}
                    onChange={(_, value) => { handleChange('timezone', value ?? '') }}
                    isOptionEqualToValue={(option, value) => option === value || value === ''}
                    getOptionLabel={getTimezoneLabel}
                    size='small'
                    sx={{ width: 380 }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder='Select timezone' />
                    )}
                  />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={LABEL_CELL}>
                  <LabelWithHint
                    label='Cross-Listed Section Report Visibility'
                    hint='Controls which survey responses are visible to instructors on cross-listed courses. Primary instructors are the lead instructor of record; secondary instructors are co-instructors on the same cross-listed section.'
                  />
                </TableCell>
                <TableCell sx={CONTROL_CELL}>
                  <RadioGroup
                    value={formState.crossListing ?? ''}
                    onChange={(e) => { handleChange('crossListing', e.target.value) }}
                  >
                    {crossListingOptions.map((option) => (
                      <FormControlLabel
                        key={option}
                        value={option}
                        control={<Radio size='small' />}
                        label={<Typography fontSize='14px'>{CROSS_LISTING_LABELS[option] ?? option}</Typography>}
                      />
                    ))}
                  </RadioGroup>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={LABEL_CELL}>
                  <LabelWithHint
                    label='Allow SIS Overrides'
                    hint='When enabled, manual changes to user data take precedence over SIS data. When disabled, the SIS can modify all entries.'
                  />
                </TableCell>
                <TableCell sx={CONTROL_CELL}>
                  <Switch
                    checked={formState.enableSisUserOverride ?? false}
                    onChange={(e) => { handleChange('enableSisUserOverride', e.target.checked) }}
                  />
                </TableCell>
              </TableRow>

              {showDevTools && (
                <TableRow>
                  <TableCell sx={LABEL_CELL}>
                    <LabelWithHint
                      label='SIS Sync'
                      hint='Manually trigger a SIS sync for this tenant. Only visible in dev/test environments.'
                    />
                  </TableCell>
                  <TableCell sx={CONTROL_CELL}>
                    <Button variant="outlined" size="small" disabled={isSisTriggering} onClick={() => { void handleSisTrigger() }}>
                      {isSisTriggering ? 'Running...' : 'Run SIS Sync'}
                    </Button>
                  </TableCell>
                </TableRow>
              )}

              <TableRow>
                <TableCell sx={LABEL_CELL}>
                  <LabelWithHint
                    label='Show Repeat Per Instructor Sections in Aggregate Reports'
                    hint='When enabled, repeat per instructor sections are rolled up across all levels of reporting, up to the institution level reports.'
                  />
                </TableCell>
                <TableCell sx={CONTROL_CELL}>
                  <Switch
                    checked={formState.enableRepeatPerInstructorMerging ?? false}
                    onChange={(e) => { handleChange('enableRepeatPerInstructorMerging', e.target.checked) }}
                  />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={LABEL_CELL}>
                  <LabelWithHint
                    label='Use Custom Branding'
                    hint="When enabled, your institution's custom logo and branding assets are applied across the platform. Note: no visible change until the ClassRanked Implementations team has set up your institution's icon assets."
                  />
                </TableCell>
                <TableCell sx={CONTROL_CELL}>
                  <Switch
                    checked={formState.isCustomBrandingEnabled ?? false}
                    onChange={(e) => { handleChange('isCustomBrandingEnabled', e.target.checked) }}
                  />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={LABEL_CELL}>
                  <LabelWithHint
                    label='Enable Live Reports for Account Owners'
                    hint='Lets Account Owners view course-section reports at any time, before the normal release date.'
                  />
                </TableCell>
                <TableCell sx={CONTROL_CELL}>
                  <Switch
                    checked={formState.isLiveReportsEnabled ?? false}
                    onChange={(e) => { handleChange('isLiveReportsEnabled', e.target.checked) }}
                  />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={LABEL_CELL}>
                  <LabelWithHint
                    label='Use Default ClassRanked Authentication'
                    hint='When enabled, all new users are provisioned via ClassRanked Authentication (Cognito) by default. When disabled, users are created with local authentication only. This can be overridden on a per-user basis at the time of creation.'
                  />
                </TableCell>
                <TableCell sx={CONTROL_CELL}>
                  <Switch
                    checked={formState.defaultCognitoProvisioned ?? false}
                    onChange={(e) => { handleChange('defaultCognitoProvisioned', e.target.checked) }}
                  />
                </TableCell>
              </TableRow>

            </TableBody>
          </Table>

          <Box display='flex' justifyContent='flex-end' gap={2} mt={2}>
            <GenericButton
              buttonType={ButtonType.Secondary}
              label='Cancel'
              fontWeight='600'
              disabled={!isDirty}
              onClick={handleCancel}
            />
            <GenericButton
              buttonType={ButtonType.Primary}
              label='Save Changes'
              fontWeight='600'
              onClick={handleSave}
              disabled={!isDirty || isSaving}
            />
          </Box>
        </Box>
      )}

      {activeTab === TabKeys.ROLE_MANAGEMENT && (
        <Suspense fallback={<LoadingPageV2 />}>
          <TabbedRoleManagement />
        </Suspense>
      )}

      {activeTab === TabKeys.PERMISSION_MANAGEMENT && (
        <Suspense fallback={<LoadingPageV2 />}>
          <Box display='flex' flexDirection='column' gap={4}>
            <PermissionManagementHeader
              onApply={(entity) => { setSelectedPermissionEntity(entity) }}
            />
            {listWithReadActionOnly && (
              <AppTable
                topbarTitle={tableHeader}
                list={listWithReadActionOnly}
                loading={isPermissionCdgLoading}
                featureName='Permission'
              />
            )}
          </Box>
        </Suspense>
      )}

      {activeTab === TabKeys.REPORT_VIEWER_ROLES && (
        <ReportViewerRolesTab />
      )}
    </Box>
  )
}

export default InstitutionalSettingsFeature
